'use client';

/** @jsxImportSource react */

import { useEffect, useRef } from 'react';

import {
  useDwell,
  useScrollDepth,
} from '@marketing/components/engagement-hooks';
import { catalog } from '@onark/observability';

/**
 * Engagement instrumentation for the v2 signup pages (/sign-up, /start/*).
 *
 * These pages shipped with NONE: `scroll_depth`, `time_on_page`, and
 * `faq_opened` fire only on the marketing app, so querying them for a signup
 * page returned zeros that look like "nobody reads this" but are really
 * "nothing is measured". That made every content/layout change here
 * unfalsifiable — the ADR 0002 failure mode on a money path.
 *
 * Mechanics come from `@onark/shared/engagement`, shared with the marketing
 * tracker; only the emit differs (catalog events keyed on `landing`, so this
 * slices with the rest of the signup funnel rather than by URL).
 *
 * ⚠️ The FAQ hook is NOT the marketing one. That tracker listens for `toggle`
 * on native `<details>`; this page's FAQ is a HeroUI/React-Aria accordion that
 * renders `<button aria-expanded>` and emits no `toggle` event at all, so the
 * ported listener would have been silently dead. Verified against the real DOM.
 */
export default function SignupEngagementTracker({
  landing,
  step,
}: {
  landing?: string;
  step: 1 | 2;
}) {
  // Read through a ref so changing step doesn't tear down and re-arm the
  // listeners (which would reset scroll milestones mid-session and double-fire
  // on the step 1 → 2 transition).
  const stepRef = useRef(step);

  stepRef.current = step;

  // Scroll depth. One tracker for the page's lifetime: step 2 replaces the
  // hero form but it's the same visit, and re-firing 25% because the DOM
  // changed would overstate reading.
  useScrollDepth(
    milestone =>
      catalog.emit('signup_trial.page_scrolled', {
        landing,
        milestone,
        step: stepRef.current,
      }),
    landing
  );

  // Active dwell. Foreground time only — a tab parked in the background is not
  // a read, and counting it would make this useless as an attention signal.
  useDwell(
    seconds => catalog.emit('signup_trial.dwelled', { landing, seconds }),
    landing
  );

  // FAQ opens — the most direct read on which objection is unresolved.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const trigger = (e.target as HTMLElement | null)?.closest?.(
        '[data-slot="accordion-trigger"]'
      );

      if (!trigger) return;
      // aria-expanded is still the PRE-click state here, so "false" is the
      // click that opens it. Without this guard every collapse counts as a read.
      if (trigger.getAttribute('aria-expanded') !== 'false') return;

      catalog.emit('signup_trial.faq_opened', {
        landing,
        question: (trigger.textContent ?? '').trim().slice(0, 120),
      });
    };

    document.addEventListener('click', onClick, { capture: true });

    return () =>
      document.removeEventListener('click', onClick, { capture: true });
  }, [landing]);

  return null;
}
